import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BookOpen, Plus, Trash, Pencil, Eye, MagnifyingGlass, X, Download,
  ArrowDown, ArrowUp, Money, Bank, Wallet, CalendarBlank, Receipt,
  ChartBar, Table, FileText, Funnel, Check, Warning, ArrowLeft, ArrowRight,
  CurrencyCircleDollar, ListBullets
} from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const fmtN = (v) => v ? Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0';
const fmtDate = (d) => { if (!d) return '-'; try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };
const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_SHORT = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ACCOUNT_HEADS = [
  'Daily Sales', 'Petro & Oil', 'Generator Oil', 'Car Rent Fees', 'Office Rent Fees',
  'Loan', 'General Expense', 'Stationary', 'Cleaning Fees', 'Transportation Fees',
  "Ye's Drawing", "Naw's Drawing", 'Salaries', 'Stock', 'Investment',
  'BN Marketing', 'ML Marketing', 'Car Repair Fees', 'ME Repair Fees', 'Office Repair Fees',
  'Internet Bill', 'License Expenses', 'Electricity Bill', 'Sofe', 'Staff Expenses',
  'Cleaning Expense', 'Other Income', 'Other Expense',
];
const COMPANIES = ['Myanmar Link', 'Burma Net', 'Ocean Net'];
const PAYMENT_METHODS = ['Cash', 'Bank'];
const TRANSACTION_TYPES = ['Income', 'Expense'];

const EMPTY_FORM = {
  date: new Date().toISOString().split('T')[0],
  voucher_no: '',
  company: 'Myanmar Link',
  account_head: 'Daily Sales',
  payment_method: 'Cash',
  transaction_type: 'Income',
  is_new: true,
  description: '',
  debit: '',
  credit: '',
  remarks: '',
};

export default function CashBookModule() {
  const [tab, setTab] = useState('transactions');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  // Transactions
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [txSearch, setTxSearch] = useState('');
  const [txFilter, setTxFilter] = useState({ company: '', account_head: '', payment_method: '', transaction_type: '' });
  const [txLoading, setTxLoading] = useState(false);

  // Form
  const [showForm, setShowForm] = useState(false);
  const [editTx, setEditTx] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  // Summary
  const [summary, setSummary] = useState(null);

  // Budget vs Actual
  const [budgetData, setBudgetData] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [editBudget, setEditBudget] = useState(null);
  const [budgetForm, setBudgetForm] = useState({ account_head: '', month: 1, year: year, budget_amount: '' });

  // Opening Balance
  const [openingBalance, setOpeningBalance] = useState({ bank: 0, cash: 0 });
  const [editOpeningBalance, setEditOpeningBalance] = useState(false);
  const [obForm, setObForm] = useState({ bank: '', cash: '' });

  const authFetch = async (url, opts = {}) => {
    const token = localStorage.getItem('session_token') || localStorage.getItem('token');
    const r = await fetch(url, { ...opts, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...(opts.headers || {}) } });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || r.status); }
    return r.json();
  };

  const loadTransactions = useCallback(async () => {
    setTxLoading(true);
    try {
      const params = new URLSearchParams({ year, month, page: txPage, limit: 50 });
      if (txSearch) params.set('search', txSearch);
      if (txFilter.company) params.set('company', txFilter.company);
      if (txFilter.account_head) params.set('account_head', txFilter.account_head);
      if (txFilter.payment_method) params.set('payment_method', txFilter.payment_method);
      if (txFilter.transaction_type) params.set('transaction_type', txFilter.transaction_type);
      const data = await authFetch(`${API}/cashbook/transactions?${params}`);
      setTransactions(data.transactions || []);
      setTxTotal(data.total || 0);
    } catch (e) { toast.error('Failed to load transactions'); }
    setTxLoading(false);
  }, [year, month, txPage, txSearch, txFilter]);

  const loadSummary = useCallback(async () => {
    try {
      const data = await authFetch(`${API}/cashbook/monthly-summary?year=${year}`);
      setSummary(data);
    } catch (e) { console.error(e); }
  }, [year]);

  const loadBudgetVsActual = useCallback(async () => {
    setBudgetLoading(true);
    try {
      const params = new URLSearchParams({ year });
      if (month) params.set('month', month);
      const data = await authFetch(`${API}/cashbook/budget-vs-actual?${params}`);
      setBudgetData(data);
    } catch (e) { toast.error('Failed to load budget data'); }
    setBudgetLoading(false);
  }, [year, month]);

  const loadOpeningBalance = useCallback(async () => {
    try {
      const data = await authFetch(`${API}/cashbook/opening-balance?year=${year}&month=${month}`);
      setOpeningBalance(data);
    } catch (e) { console.error(e); }
  }, [year, month]);

  useEffect(() => {
    if (tab === 'transactions') loadTransactions();
    if (tab === 'summary') loadSummary();
    if (tab === 'budget') loadBudgetVsActual();
  }, [tab, loadTransactions, loadSummary, loadBudgetVsActual]);

  // Explicit reload when month or year changes
  useEffect(() => {
    loadTransactions();
    loadSummary();
    loadBudgetVsActual();
    loadOpeningBalance();
  }, [month, year]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadOpeningBalance();
  }, [loadOpeningBalance]);

  const openAddForm = async () => {
    try {
      const data = await authFetch(`${API}/cashbook/next-voucher?transaction_type=Expense`);
      setForm({ ...EMPTY_FORM, voucher_no: data.voucher_no, date: new Date().toISOString().split('T')[0] });
    } catch {
      setForm({ ...EMPTY_FORM });
    }
    setEditTx(null);
    setShowForm(true);
  };

  const openEditForm = (tx) => {
    setForm({
      date: tx.date,
      voucher_no: tx.voucher_no,
      company: tx.company,
      account_head: tx.account_head,
      payment_method: tx.payment_method,
      transaction_type: tx.transaction_type,
      is_new: tx.is_new,
      description: tx.description,
      debit: tx.debit || '',
      credit: tx.credit || '',
      remarks: tx.remarks || '',
    });
    setEditTx(tx);
    setShowForm(true);
  };

  const saveTransaction = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (!form.debit && !form.credit) { toast.error('Enter Debit or Credit amount'); return; }
    setSaving(true);
    try {
      const payload = { ...form, debit: parseFloat(form.debit) || 0, credit: parseFloat(form.credit) || 0 };
      if (editTx) {
        await authFetch(`${API}/cashbook/transactions/${editTx.transaction_id}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('Transaction updated');
      } else {
        const res = await authFetch(`${API}/cashbook/transactions`, { method: 'POST', body: JSON.stringify(payload) });
        toast.success(`Transaction saved — ${res.voucher_no}`);
      }
      setShowForm(false);
      loadTransactions();
      loadSummary();
    } catch (e) { toast.error(e.message || 'Save failed'); }
    setSaving(false);
  };

  const deleteTx = async (tx) => {
    if (!window.confirm(`Delete ${tx.voucher_no}?`)) return;
    try {
      await authFetch(`${API}/cashbook/transactions/${tx.transaction_id}`, { method: 'DELETE' });
      toast.success('Deleted');
      loadTransactions();
      loadSummary();
    } catch (e) { toast.error('Delete failed'); }
  };

  const saveBudget = async () => {
    try {
      await authFetch(`${API}/cashbook/budget`, { method: 'POST', body: JSON.stringify({ ...budgetForm, year: parseInt(budgetForm.year), month: parseInt(budgetForm.month), budget_amount: parseFloat(budgetForm.budget_amount) || 0 }) });
      toast.success('Budget saved');
      setEditBudget(null);
      loadBudgetVsActual();
    } catch (e) { toast.error('Failed to save budget'); }
  };

  const saveOpeningBalance = async () => {
    try {
      await authFetch(`${API}/cashbook/opening-balance`, { method: 'POST', body: JSON.stringify({ year, month, bank: parseFloat(obForm.bank) || 0, cash: parseFloat(obForm.cash) || 0 }) });
      toast.success('Opening balance saved');
      setEditOpeningBalance(false);
      loadOpeningBalance();
    } catch (e) { toast.error('Failed to save opening balance'); }
  };

  // Totals for current view
  const totalDebit = transactions.reduce((s, t) => s + (t.debit || 0), 0);
  const totalCredit = transactions.reduce((s, t) => s + (t.credit || 0), 0);
  const totalIncome = transactions.filter(t => t.transaction_type === 'Income').reduce((s, t) => s + (t.debit || 0) + (t.credit || 0), 0);
  const totalExpense = transactions.filter(t => t.transaction_type === 'Expense').reduce((s, t) => s + (t.debit || 0) + (t.credit || 0), 0);

  return (
    <div className="p-4 max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <BookOpen size={22} className="text-[#1565C0]" weight="fill" />
            Cash Book — Pay List
          </h2>
          <p className="text-[11px] text-[var(--md-on-surface-variant)]">Myanmar Link Company · {MONTH_NAMES[month]} {year}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Year/Month selector */}
          <select value={year} onChange={e => { const v = parseInt(e.target.value); setYear(v); }} onInput={e => { const v = parseInt(e.target.value); setYear(v); }} className="md3-input text-xs py-1.5 px-2 w-24">
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={month} onChange={e => { const v = parseInt(e.target.value); setMonth(v); }} onInput={e => { const v = parseInt(e.target.value); setMonth(v); }} className="md3-input text-xs py-1.5 px-2 w-28">
            {MONTH_NAMES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded-xl border-2 border-[#A5D6A7] p-3 shadow-sm">
          <p className="text-[10px] uppercase text-[var(--md-on-surface-variant)]">Total Income</p>
          <p className="text-lg font-bold text-[#006D3B] tabular-nums">{fmtN(totalIncome)}</p>
          <p className="text-[10px] text-[var(--md-on-surface-variant)]">MMK</p>
        </div>
        <div className="bg-white rounded-xl border-2 border-[#EF9A9A] p-3 shadow-sm">
          <p className="text-[10px] uppercase text-[var(--md-on-surface-variant)]">Total Expense</p>
          <p className="text-lg font-bold text-[#BA1A1A] tabular-nums">{fmtN(totalExpense)}</p>
          <p className="text-[10px] text-[var(--md-on-surface-variant)]">MMK</p>
        </div>
        <div className="bg-white rounded-xl border-2 border-[#BBDEFB] p-3 shadow-sm">
          <p className="text-[10px] uppercase text-[var(--md-on-surface-variant)]">Net Balance</p>
          <p className={`text-lg font-bold tabular-nums ${totalIncome - totalExpense >= 0 ? 'text-[#006D3B]' : 'text-[#BA1A1A]'}`}>{fmtN(totalIncome - totalExpense)}</p>
          <p className="text-[10px] text-[var(--md-on-surface-variant)]">MMK</p>
        </div>
        <div className="bg-white rounded-xl border p-3 shadow-sm">
          <p className="text-[10px] uppercase text-[var(--md-on-surface-variant)]">Opening Balance</p>
          <div className="flex gap-2 text-[10px] mt-1">
            <span className="text-[#1565C0] font-bold">Bank: {fmtN(openingBalance.bank)}</span>
            <span className="text-[#006D3B] font-bold">Cash: {fmtN(openingBalance.cash)}</span>
          </div>
          <button onClick={() => { setObForm({ bank: openingBalance.bank, cash: openingBalance.cash }); setEditOpeningBalance(true); }} className="text-[9px] text-[var(--md-primary)] underline mt-1">Edit</button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[var(--md-surface-1)] rounded-xl p-1 flex flex-wrap gap-1 h-auto mb-4">
          <TabsTrigger value="transactions" className="rounded-lg data-[state=active]:bg-[#1565C0] data-[state=active]:text-white text-xs"><ListBullets size={14} className="mr-1" />Transactions</TabsTrigger>
          <TabsTrigger value="summary" className="rounded-lg data-[state=active]:bg-[#006D3B] data-[state=active]:text-white text-xs"><ChartBar size={14} className="mr-1" />Monthly Summary</TabsTrigger>
          <TabsTrigger value="budget" className="rounded-lg data-[state=active]:bg-[#7C4DFF] data-[state=active]:text-white text-xs"><Table size={14} className="mr-1" />Budget vs Actual</TabsTrigger>
        </TabsList>

        {/* ===== TRANSACTIONS TAB ===== */}
        <TabsContent value="transactions" className="space-y-3">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--md-on-surface-variant)]" />
              <input value={txSearch} onChange={e => { setTxSearch(e.target.value); setTxPage(1); }} placeholder="Search voucher, description..." className="md3-input pl-8 text-xs py-2 w-full" />
            </div>
            <select value={txFilter.transaction_type} onChange={e => setTxFilter({ ...txFilter, transaction_type: e.target.value })} className="md3-input text-xs py-2 px-2">
              <option value="">All Types</option>
              {TRANSACTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={txFilter.payment_method} onChange={e => setTxFilter({ ...txFilter, payment_method: e.target.value })} className="md3-input text-xs py-2 px-2">
              <option value="">Cash & Bank</option>
              {PAYMENT_METHODS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={txFilter.account_head} onChange={e => setTxFilter({ ...txFilter, account_head: e.target.value })} className="md3-input text-xs py-2 px-2">
              <option value="">All Accounts</option>
              {ACCOUNT_HEADS.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
            <select value={txFilter.company} onChange={e => setTxFilter({ ...txFilter, company: e.target.value })} className="md3-input text-xs py-2 px-2">
              <option value="">All Companies</option>
              {COMPANIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={openAddForm} className="md3-filled-btn text-xs flex items-center gap-1 whitespace-nowrap">
              <Plus size={14} /> Add Entry
            </button>
          </div>

          {/* Transaction Table */}
          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-[#1565C0] text-white">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-semibold">Date</th>
                    <th className="text-left px-3 py-2.5 font-semibold">Vr. No</th>
                    <th className="text-left px-3 py-2.5 font-semibold">Company</th>
                    <th className="text-left px-3 py-2.5 font-semibold">Account Head</th>
                    <th className="text-left px-3 py-2.5 font-semibold">Payment</th>
                    <th className="text-left px-3 py-2.5 font-semibold">Type</th>
                    <th className="text-left px-3 py-2.5 font-semibold">Description</th>
                    <th className="text-right px-3 py-2.5 font-semibold">Debit</th>
                    <th className="text-right px-3 py-2.5 font-semibold">Credit</th>
                    <th className="text-center px-3 py-2.5 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {txLoading ? (
                    <tr><td colSpan={10} className="text-center py-8 text-[var(--md-on-surface-variant)]">Loading...</td></tr>
                  ) : transactions.length === 0 ? (
                    <tr><td colSpan={10} className="text-center py-10 text-[var(--md-on-surface-variant)]">
                      <BookOpen size={32} className="mx-auto mb-2 text-[var(--md-outline)]" />
                      No transactions found. Click "Add Entry" to start.
                    </td></tr>
                  ) : transactions.map((tx, i) => (
                    <tr key={tx.transaction_id} className={`border-b border-[var(--md-outline-variant)] hover:bg-[var(--md-surface-1)] ${i % 2 === 0 ? '' : 'bg-[#FAFAFA]'}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(tx.date)}</td>
                      <td className="px-3 py-2 font-mono font-bold text-[#1565C0] whitespace-nowrap">{tx.voucher_no}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{tx.company}</td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-0.5 rounded-full text-[9px] bg-[var(--md-surface-2)]">{tx.account_head}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${tx.payment_method === 'Bank' ? 'bg-[#E3F2FD] text-[#1565C0]' : 'bg-[#E8F5E9] text-[#006D3B]'}`}>{tx.payment_method}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${tx.transaction_type === 'Income' ? 'bg-[#E8F5E9] text-[#006D3B]' : 'bg-[#FFEBEE] text-[#BA1A1A]'}`}>{tx.transaction_type}</span>
                      </td>
                      <td className="px-3 py-2 max-w-[200px] truncate" title={tx.description}>{tx.description}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-[#BA1A1A]">{tx.debit > 0 ? fmtN(tx.debit) : '-'}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-[#006D3B]">{tx.credit > 0 ? fmtN(tx.credit) : '-'}</td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex justify-center gap-1">
                          <button onClick={() => openEditForm(tx)} className="p-1 rounded-lg hover:bg-[#E3F2FD] text-[#1565C0]"><Pencil size={13} /></button>
                          <button onClick={() => deleteTx(tx)} className="p-1 rounded-lg hover:bg-[#FFEBEE] text-[#BA1A1A]"><Trash size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {transactions.length > 0 && (
                  <tfoot className="bg-[#F5F5F5] border-t-2 border-[var(--md-outline-variant)]">
                    <tr>
                      <td colSpan={7} className="px-3 py-2 font-bold text-xs">Total ({transactions.length} entries)</td>
                      <td className="px-3 py-2 text-right font-bold font-mono text-[#BA1A1A]">{fmtN(totalDebit)}</td>
                      <td className="px-3 py-2 text-right font-bold font-mono text-[#006D3B]">{fmtN(totalCredit)}</td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {/* Pagination */}
            {txTotal > 50 && (
              <div className="flex items-center justify-between px-4 py-2 border-t text-xs">
                <span className="text-[var(--md-on-surface-variant)]">{txTotal} total entries</span>
                <div className="flex gap-2">
                  <button onClick={() => setTxPage(p => Math.max(1, p - 1))} disabled={txPage === 1} className="px-3 py-1 rounded-lg border disabled:opacity-40"><ArrowLeft size={12} /></button>
                  <span className="px-2 py-1">Page {txPage}</span>
                  <button onClick={() => setTxPage(p => p + 1)} disabled={txPage * 50 >= txTotal} className="px-3 py-1 rounded-lg border disabled:opacity-40"><ArrowRight size={12} /></button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ===== MONTHLY SUMMARY TAB ===== */}
        <TabsContent value="summary" className="space-y-4">
          {summary ? (
            <>
              {/* Chart */}
              <div className="bg-white rounded-2xl border p-4 shadow-sm">
                <h3 className="text-sm font-bold mb-3">Income vs Expense — {year}</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={summary.summary.filter(m => m.income_bank + m.income_cash + m.expense_bank + m.expense_cash > 0)} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--md-outline-variant)" vertical={false} />
                    <XAxis dataKey="month_name" tickFormatter={v => v.slice(0, 3)} axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10 }} tickFormatter={v => `${(v / 1000000).toFixed(1)}M`} />
                    <Tooltip formatter={(v) => fmtN(v) + ' MMK'} contentStyle={{ borderRadius: 8, fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="income_cash" name="Income (Cash)" fill="#006D3B" radius={[3, 3, 0, 0]} stackId="income" />
                    <Bar dataKey="income_bank" name="Income (Bank)" fill="#4CAF50" radius={[3, 3, 0, 0]} stackId="income" />
                    <Bar dataKey="expense_cash" name="Expense (Cash)" fill="#BA1A1A" radius={[3, 3, 0, 0]} stackId="expense" />
                    <Bar dataKey="expense_bank" name="Expense (Bank)" fill="#EF5350" radius={[3, 3, 0, 0]} stackId="expense" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Summary Table — like Google Sheets Summary tab */}
              <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
                <div className="bg-[#1565C0] text-white px-4 py-2.5">
                  <h3 className="text-sm font-bold">Summary Income & Expense — {year}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-[#E3F2FD]">
                      <tr>
                        <th className="text-center px-3 py-2 font-bold border-r">No</th>
                        <th className="text-left px-3 py-2 font-bold border-r">Monthly</th>
                        <th className="text-right px-3 py-2 font-bold text-[#006D3B]" colSpan={2}>Total Income</th>
                        <th className="text-right px-3 py-2 font-bold text-[#BA1A1A]" colSpan={2}>Total Expense</th>
                        <th className="text-right px-3 py-2 font-bold text-[#1565C0]" colSpan={2}>Total Balance</th>
                      </tr>
                      <tr className="bg-[#BBDEFB] text-[10px]">
                        <th colSpan={2} className="border-r" />
                        <th className="text-right px-3 py-1 text-[#1565C0]">Bank</th>
                        <th className="text-right px-3 py-1 text-[#006D3B] border-r">Cash (MMK)</th>
                        <th className="text-right px-3 py-1 text-[#1565C0]">Bank</th>
                        <th className="text-right px-3 py-1 text-[#BA1A1A] border-r">Cash (MMK)</th>
                        <th className="text-right px-3 py-1 text-[#1565C0]">Bank</th>
                        <th className="text-right px-3 py-1">Cash (MMK)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.summary.map((m, i) => (
                        <tr key={m.no} className={`border-b border-[var(--md-outline-variant)] ${i % 2 === 0 ? '' : 'bg-[#FAFAFA]'}`}>
                          <td className="px-3 py-2 text-center text-[var(--md-on-surface-variant)] border-r">{m.no}</td>
                          <td className="px-3 py-2 font-bold border-r">{m.month_name}</td>
                          <td className="px-3 py-2 text-right font-mono text-[#1565C0]">{m.income_bank > 0 ? fmtN(m.income_bank) : '-'}</td>
                          <td className="px-3 py-2 text-right font-mono text-[#006D3B] border-r">{m.income_cash > 0 ? fmtN(m.income_cash) : '-'}</td>
                          <td className="px-3 py-2 text-right font-mono text-[#1565C0]">{m.expense_bank > 0 ? fmtN(m.expense_bank) : '-'}</td>
                          <td className="px-3 py-2 text-right font-mono text-[#BA1A1A] border-r">{m.expense_cash > 0 ? fmtN(m.expense_cash) : '-'}</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${m.balance_bank >= 0 ? 'text-[#006D3B]' : 'text-[#BA1A1A]'}`}>{fmtN(m.balance_bank)}</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${m.balance_cash >= 0 ? 'text-[#006D3B]' : 'text-[#BA1A1A]'}`}>{fmtN(m.balance_cash)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-[#E3F2FD] border-t-2">
                      <tr>
                        <td colSpan={2} className="px-3 py-2 font-bold border-r">TOTAL</td>
                        <td className="px-3 py-2 text-right font-bold font-mono text-[#1565C0]">{fmtN(summary.summary.reduce((s, m) => s + m.income_bank, 0))}</td>
                        <td className="px-3 py-2 text-right font-bold font-mono text-[#006D3B] border-r">{fmtN(summary.summary.reduce((s, m) => s + m.income_cash, 0))}</td>
                        <td className="px-3 py-2 text-right font-bold font-mono text-[#1565C0]">{fmtN(summary.summary.reduce((s, m) => s + m.expense_bank, 0))}</td>
                        <td className="px-3 py-2 text-right font-bold font-mono text-[#BA1A1A] border-r">{fmtN(summary.summary.reduce((s, m) => s + m.expense_cash, 0))}</td>
                        <td className={`px-3 py-2 text-right font-bold font-mono ${summary.summary.reduce((s, m) => s + m.balance_bank, 0) >= 0 ? 'text-[#006D3B]' : 'text-[#BA1A1A]'}`}>{fmtN(summary.summary.reduce((s, m) => s + m.balance_bank, 0))}</td>
                        <td className={`px-3 py-2 text-right font-bold font-mono ${summary.summary.reduce((s, m) => s + m.balance_cash, 0) >= 0 ? 'text-[#006D3B]' : 'text-[#BA1A1A]'}`}>{fmtN(summary.summary.reduce((s, m) => s + m.balance_cash, 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-10 text-[var(--md-on-surface-variant)]">Loading summary...</div>
          )}
        </TabsContent>

        {/* ===== BUDGET VS ACTUAL TAB ===== */}
        <TabsContent value="budget" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold">Budget vs Actual — {MONTH_NAMES[month]} {year}</h3>
            <button onClick={() => { setBudgetForm({ account_head: ACCOUNT_HEADS[0], month, year, budget_amount: '' }); setEditBudget(true); }} className="md3-outlined-btn text-xs flex items-center gap-1">
              <Pencil size={13} /> Set Budget
            </button>
          </div>
          {budgetLoading ? (
            <div className="text-center py-10 text-[var(--md-on-surface-variant)]">Loading...</div>
          ) : budgetData ? (
            <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-[#7C4DFF] text-white">
                    <tr>
                      <th className="text-left px-3 py-2.5 font-semibold">Account Head</th>
                      <th className="text-right px-3 py-2.5 font-semibold">Budget (MMK)</th>
                      <th className="text-right px-3 py-2.5 font-semibold">Actual Use</th>
                      <th className="text-right px-3 py-2.5 font-semibold">Balance</th>
                      <th className="text-center px-3 py-2.5 font-semibold">Usage %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {budgetData.data.map((row, i) => {
                      const m = row.months[0] || {};
                      const pct = m.budget > 0 ? Math.round((m.actual / m.budget) * 100) : 0;
                      const over = m.balance < 0;
                      if (m.budget === 0 && m.actual === 0) return null;
                      return (
                        <tr key={row.account_head} className={`border-b border-[var(--md-outline-variant)] ${i % 2 === 0 ? '' : 'bg-[#FAFAFA]'}`}>
                          <td className="px-3 py-2 font-medium">{row.account_head}</td>
                          <td className="px-3 py-2 text-right font-mono">{m.budget > 0 ? fmtN(m.budget) : <span className="text-[var(--md-outline)]">—</span>}</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${m.actual > 0 ? 'text-[#BA1A1A]' : 'text-[var(--md-on-surface-variant)]'}`}>{m.actual > 0 ? fmtN(m.actual) : '—'}</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${over ? 'text-[#BA1A1A]' : 'text-[#006D3B]'}`}>{m.budget > 0 ? fmtN(m.balance) : '—'}</td>
                          <td className="px-3 py-2">
                            {m.budget > 0 ? (
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-[var(--md-surface-2)] rounded-full h-2">
                                  <div className={`h-2 rounded-full ${pct > 100 ? 'bg-[#BA1A1A]' : pct > 80 ? 'bg-[#F57F17]' : 'bg-[#006D3B]'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                                </div>
                                <span className={`text-[9px] font-bold w-8 text-right ${pct > 100 ? 'text-[#BA1A1A]' : ''}`}>{pct}%</span>
                              </div>
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </TabsContent>
      </Tabs>

      {/* ===== ADD/EDIT TRANSACTION FORM ===== */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b bg-[#1565C0] text-white rounded-t-2xl">
              <h3 className="font-bold text-sm">{editTx ? 'Edit Transaction' : 'New Cash Book Entry'}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-white/20 rounded-lg"><X size={18} /></button>
            </div>
            <form onSubmit={saveTransaction} className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">Date *</label>
                  <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required className="md3-input text-xs w-full" />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">Voucher No</label>
                  <input value={form.voucher_no} onChange={e => setForm({ ...form, voucher_no: e.target.value })} className="md3-input text-xs w-full font-mono" placeholder="PV-00001" />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">Company *</label>
                  <select value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} required className="md3-input text-xs w-full">
                    {COMPANIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">Account Head *</label>
                  <select value={form.account_head} onChange={e => setForm({ ...form, account_head: e.target.value })} required className="md3-input text-xs w-full">
                    {ACCOUNT_HEADS.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">Payment Method *</label>
                  <div className="flex gap-2">
                    {PAYMENT_METHODS.map(p => (
                      <button key={p} type="button" onClick={() => setForm({ ...form, payment_method: p })}
                        className={`flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all ${form.payment_method === p ? (p === 'Bank' ? 'bg-[#1565C0] text-white border-[#1565C0]' : 'bg-[#006D3B] text-white border-[#006D3B]') : 'border-[var(--md-outline-variant)] text-[var(--md-on-surface-variant)]'}`}>
                        {p === 'Bank' ? <Bank size={14} className="inline mr-1" /> : <Wallet size={14} className="inline mr-1" />}{p}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">Transaction Type *</label>
                  <div className="flex gap-2">
                    {TRANSACTION_TYPES.map(t => (
                      <button key={t} type="button" onClick={() => setForm({ ...form, transaction_type: t })}
                        className={`flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all ${form.transaction_type === t ? (t === 'Income' ? 'bg-[#006D3B] text-white border-[#006D3B]' : 'bg-[#BA1A1A] text-white border-[#BA1A1A]') : 'border-[var(--md-outline-variant)] text-[var(--md-on-surface-variant)]'}`}>
                        {t === 'Income' ? <ArrowDown size={14} className="inline mr-1" /> : <ArrowUp size={14} className="inline mr-1" />}{t}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">Debit (MMK)</label>
                  <input type="number" value={form.debit} onChange={e => setForm({ ...form, debit: e.target.value })} className="md3-input text-xs w-full font-mono" placeholder="0" min="0" />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">Credit (MMK)</label>
                  <input type="number" value={form.credit} onChange={e => setForm({ ...form, credit: e.target.value })} className="md3-input text-xs w-full font-mono" placeholder="0" min="0" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">Description *</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required className="md3-input text-xs w-full" placeholder="e.g. MMGD005D1 New Installation, Petro & Oil payment..." />
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={form.is_new} onChange={e => setForm({ ...form, is_new: e.target.checked })} className="rounded" />
                  <span>New (uncheck for Old/Existing)</span>
                </label>
              </div>
              <div>
                <label className="text-[10px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">Remarks</label>
                <input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} className="md3-input text-xs w-full" placeholder="Optional remarks..." />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="md3-outlined-btn flex-1 text-xs">Cancel</button>
                <button type="submit" disabled={saving} className="md3-filled-btn flex-1 text-xs">
                  {saving ? 'Saving...' : editTx ? 'Update Entry' : 'Save Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== SET BUDGET DIALOG ===== */}
      {editBudget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm">Set Budget</h3>
              <button onClick={() => setEditBudget(null)} className="p-1 rounded-lg hover:bg-[var(--md-surface-2)]"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-medium mb-1 block">Account Head</label>
                <select value={budgetForm.account_head} onChange={e => setBudgetForm({ ...budgetForm, account_head: e.target.value })} className="md3-input text-xs w-full">
                  {ACCOUNT_HEADS.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-medium mb-1 block">Month</label>
                  <select value={budgetForm.month} onChange={e => setBudgetForm({ ...budgetForm, month: parseInt(e.target.value) })} className="md3-input text-xs w-full">
                    {MONTH_NAMES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium mb-1 block">Year</label>
                  <input type="number" value={budgetForm.year} onChange={e => setBudgetForm({ ...budgetForm, year: parseInt(e.target.value) })} className="md3-input text-xs w-full" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-medium mb-1 block">Budget Amount (MMK)</label>
                <input type="number" value={budgetForm.budget_amount} onChange={e => setBudgetForm({ ...budgetForm, budget_amount: e.target.value })} className="md3-input text-xs w-full font-mono" placeholder="e.g. 3000000" />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => setEditBudget(null)} className="md3-outlined-btn flex-1 text-xs">Cancel</button>
                <button onClick={saveBudget} className="md3-filled-btn flex-1 text-xs">Save Budget</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== OPENING BALANCE DIALOG ===== */}
      {editOpeningBalance && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm">Opening Balance — {MONTH_NAMES[month]} {year}</h3>
              <button onClick={() => setEditOpeningBalance(false)} className="p-1 rounded-lg hover:bg-[var(--md-surface-2)]"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-medium mb-1 block">Bank Balance (MMK)</label>
                <input type="number" value={obForm.bank} onChange={e => setObForm({ ...obForm, bank: e.target.value })} className="md3-input text-xs w-full font-mono" placeholder="0" />
              </div>
              <div>
                <label className="text-[10px] font-medium mb-1 block">Cash Balance (MMK)</label>
                <input type="number" value={obForm.cash} onChange={e => setObForm({ ...obForm, cash: e.target.value })} className="md3-input text-xs w-full font-mono" placeholder="0" />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => setEditOpeningBalance(false)} className="md3-outlined-btn flex-1 text-xs">Cancel</button>
                <button onClick={saveOpeningBalance} className="md3-filled-btn flex-1 text-xs">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
